// COMPLETE src/components/RealFleetMileageChart.tsx - WITH ENHANCED TOOLTIP

import React, { useState } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell } from 'recharts';
import MileageDetailModal from './MileageDetailModal';

interface Props {
  data: any;
}

// ✅ NEW: Custom Tooltip with vehicle breakdown
const CustomMileageTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const MAX_VEHICLES_SHOWN = 5;

  // Sort vehicles by mileage (highest first)
  const sortedVehicles = data.vehicles 
    ? [...data.vehicles].sort((a: any, b: any) => b.mileage - a.mileage)
    : [];
  
  const topVehicles = sortedVehicles.slice(0, MAX_VEHICLES_SHOWN);
  const remainingCount = sortedVehicles.length - MAX_VEHICLES_SHOWN;

  return (
    <Box
      sx={{
        bgcolor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        p: 1.5,
        boxShadow: 2,
        minWidth: 240,
        maxWidth: 350,
        maxHeight: 400,
        overflow: 'auto',
      }}
    >
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
        {label}
      </Typography>
      
      <Typography variant="body2" color="primary" fontWeight="600" sx={{ mb: 1 }}>
        Total: {data.mileage?.toLocaleString() || 0} km
      </Typography>

      {topVehicles.length > 0 && (
        <Box sx={{ borderTop: '1px solid #f0f0f0', pt: 1, mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {sortedVehicles.length > MAX_VEHICLES_SHOWN 
              ? `Top ${MAX_VEHICLES_SHOWN} Contributors:` 
              : 'Vehicle Breakdown:'}
          </Typography>
          {topVehicles.map((vehicle: any, idx: number) => (
            <Box 
              key={idx} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                py: 0.5,
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 600,
                    display: 'block',
                    lineHeight: 1.3,
                  }}
                >
                  {vehicle.plateNumber || vehicle.imei}
                </Typography>
                {(vehicle.make || vehicle.model) && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: '0.65rem',
                      display: 'block',
                      lineHeight: 1.2,
                    }}
                  >
                    {vehicle.make} {vehicle.model}
                  </Typography>
                )}
              </Box>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                  pt: 0.25,
                }}
              >
                {vehicle.mileage?.toLocaleString() || 0} km
              </Typography>
            </Box>
          ))}
          
          {remainingCount > 0 && (
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                py: 0.5,
                mt: 0.5,
                borderTop: '1px dashed #e0e0e0',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                + {remainingCount} more vehicle{remainingCount > 1 ? 's' : ''}
              </Typography>
              <Typography variant="caption" color="primary" fontWeight="600">
                Click for details
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {data.vehicleCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
          {data.vehicleCount} vehicle{data.vehicleCount > 1 ? 's' : ''} active
        </Typography>
      )}
    </Box>
  );
};

const RealFleetMileageChart: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState<any>(null);

  console.log('🔍 Fleet Mileage Data:', {
    hasData: !!data,
    hasFleetDaily: !!data?.fleetDaily,
    fleetDailyLength: data?.fleetDaily?.length,
    fullData: data
  });

  if (!data || !data.fleetDaily || data.fleetDaily.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary" variant="body2" textAlign="center">
          No mileage data available
        </Typography>
      </Box>
    );
  }

  // Format data for vertical bar chart
  const chartData = data.fleetDaily
    .map((item: any) => {
      const date = new Date(item.date);
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return {
        day: dayNames[date.getDay()],
        mileage: item.totalMileage || 0,
        date: item.date,
        fullDate: item.date,
        vehicleCount: item.vehicleCount || 0,
        avgMileagePerVehicle: item.avgMileagePerVehicle || 0,
        vehicles: item.vehicles || []
      };
    })
    .slice(-7); // Last 7 days

  console.log('📊 Chart Data:', chartData);

  const maxMileage = Math.max(...chartData.map((d: any) => d.mileage));
  const avgMileage = chartData.reduce((sum: number, d: any) => sum + d.mileage, 0) / chartData.length;

  // Format large numbers (e.g., 92767 → "92.8k")
  const formatMileage = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };

  // Handle bar click
  const handleChartClick = (chartData: any) => {
    if (chartData && chartData.activePayload && chartData.activePayload[0]) {
      const dayData = chartData.activePayload[0].payload;
      setSelectedDayData({
        date: dayData.fullDate,
        totalMileage: dayData.mileage,
        vehicleCount: dayData.vehicleCount,
        avgMileagePerVehicle: dayData.avgMileagePerVehicle,
        vehicles: dayData.vehicles,
      });
      setModalOpen(true);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 200 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart 
          data={chartData} 
          onClick={handleChartClick}
          margin={{ 
            top: 10, 
            right: isMobile ? 10 : 20, 
            left: isMobile ? 0 : 20, 
            bottom: isMobile ? 20 : 30 
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
          <XAxis 
            dataKey="day" 
            axisLine={false} 
            tickLine={false}
            tick={{ fontSize: isMobile ? 10 : 12 }}
          />
          <YAxis 
            axisLine={false} 
            tickLine={false}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            tickFormatter={formatMileage}
            width={isMobile ? 40 : 60}
          />
          {/* ✅ UPDATED: Use custom tooltip */}
          <Tooltip content={<CustomMileageTooltip />} />
          <Bar 
            dataKey="mileage" 
            radius={[8, 8, 0, 0]}
            cursor="pointer"
          >
            {chartData.map((entry: any, index: number) => (
              <Cell 
                key={`cell-${index}`} 
                fill={entry.mileage > avgMileage ? '#2196f3' : '#64b5f6'} 
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Mileage Detail Modal */}
      <MileageDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        date={selectedDayData?.date || ''}
        data={selectedDayData}
      />
    </Box>
  );
};

export default RealFleetMileageChart;